import { useState } from "react";
import { Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + (m || 0); };
const errMsg = (e) => { const d = e.response?.data?.detail; return typeof d === "object" ? d.message : formatApiError(d); };

// Book an open slot (adjust start/length, pick the offering for buffer time) or cancel your own reservation.
export const SlotBookDialog = ({ pick, offerings, onClose, onDone }) => {
  const [start, setStart] = useState(pick.hold ? "" : hhmm(pick.start));
  const [len, setLen] = useState(60);
  const [svc, setSvc] = useState("");
  const [busy, setBusy] = useState(false);
  const off = offerings.find((o) => o.id === svc);
  const s = toMin(start || "00:00"), e = s + Number(len || 0);
  const bb = off?.buffer_before_min || 0, ba = off?.buffer_after_min || 0;

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/enhancer/venue/holds", { room_key: pick.room.key, date: pick.date, start_time: start, end_time: hhmm(e), service_id: svc, title: off?.title || "" });
      toast.success(data.needs_approval ? "Request sent to the center manager for approval" : "Booked — it's on the center calendar");
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await api.delete(`/enhancer/venue/holds/${pick.hold.hold_id}`); toast.success("Reservation cancelled"); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="slot-dialog">
      <div className="bg-white rounded-3xl p-6 w-full max-w-md" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-serif text-2xl text-[#1A242B]">{pick.hold ? "Your reservation" : `Book ${pick.room.name}`}</h3>
            <p className="text-sm text-[#4A5D6B]">{new Date(`${pick.date}T12:00:00`).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="slot-dialog-close"><X size={18} /></button>
        </div>
        {pick.hold ? (
          <>
            <p className="text-sm text-[#1A242B] mb-4">{pick.hold.label} · {(pick.hold.session || [pick.hold.start, pick.hold.end]).join("–")}{pick.hold.status === "pending" ? " · awaiting the center manager" : ""}</p>
            <button onClick={cancel} disabled={busy} className="eyw-btn-ghost !border-red-300 !text-red-600 w-full justify-center" data-testid="slot-cancel-hold">{busy ? <Loader2 className="animate-spin" size={15} /> : null} Cancel this reservation</button>
          </>
        ) : (
          <div className="space-y-3">
            <label className="block text-sm">Offering (sets buffer time)
              <select value={svc} onChange={(ev) => { setSvc(ev.target.value); const o = offerings.find((x) => x.id === ev.target.value); if (o) setLen(o.duration_min); }} className="eyw-input w-full mt-1" data-testid="slot-offering">
                <option value="">No specific offering (no buffer)</option>
                {offerings.map((o) => <option key={o.id} value={o.id}>{o.title} · {o.duration_min}m{o.buffer_before_min || o.buffer_after_min ? ` (+${o.buffer_before_min}/${o.buffer_after_min}m buffer)` : ""}</option>)}
              </select>
            </label>
            <div className="flex gap-3">
              <label className="block text-sm flex-1">Start<input type="time" step="900" value={start} onChange={(ev) => setStart(ev.target.value)} className="eyw-input w-full mt-1" data-testid="slot-start" /></label>
              <label className="block text-sm flex-1">Length (min)<input type="number" min="15" step="15" value={len} onChange={(ev) => setLen(ev.target.value)} className="eyw-input w-full mt-1" data-testid="slot-length" /></label>
            </div>
            <p className="text-xs text-[#4A5D6B]" data-testid="slot-summary">
              Session {start}–{hhmm(e)}{bb || ba ? ` · room held ${hhmm(s - bb)}–${hhmm(e + ba)} including buffer` : ""}.
              {pick.room.requires_approval ? " This space needs the center manager's approval." : " Confirms instantly if the room is free."}
            </p>
            {pick.conflict && <p className="text-xs text-[#C0563A] flex gap-1.5" data-testid="slot-conflict-warning"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> You already have "{pick.conflict.label}" {pick.conflict.start}–{pick.conflict.end}{pick.conflict.where ? ` at ${pick.conflict.where}` : ""}.</p>}
            <button onClick={book} disabled={busy || !start || e <= s} className="eyw-btn w-full justify-center" data-testid="slot-book-confirm">
              {busy && <Loader2 className="animate-spin" size={15} />} {pick.room.requires_approval ? "Request this time" : "Book this time"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
